import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './assets/contexts/AuthContext';
import { ThemeProvider } from './assets/contexts/ThemeContext';
import { PageLayoutProvider } from './assets/contexts/PageLayoutContext';
import { PopupProvider } from './assets/contexts/PopupContext';
import { RightSidebarProvider } from './assets/contexts/RightSidebarContext';
import MainLayout from './sites/default/layouts/MainLayout';
import SidebarCommon from './core/sidebar/SidebarCommon';
import ScrollToTop from './core/scroll/ScrollToTop';
import ScrollToTopBtn from './core/scroll/ScrollToTopBtn';
import ContentSkeleton from './components/skeleton/ContentSkeleton';
import Popup from './components/popup/Popup';
import ApiHealthBanner from './components/widgets/ApiHealthBanner';
import './assets/utils/comm/i18n'; // i18n 초기화
import LanguageRouteSync from './assets/utils/comm/LanguageRouteSync';
import { isNoticePopupWindowPath } from './sites/default/pages/support/noticePopupUtils';

// 성능 최적화: React.lazy로 코드 스플리팅
const Home = lazy(() => import('./sites/default/pages/Home'));
const Login = lazy(() => import('./sites/default/pages/Login'));
const PageRouter = lazy(() => import('./sites/default/pages/comm/PageRouter'));
const Register = lazy(() => import('./sites/default/pages/Register'));
const RegisterTypeSelect = lazy(() => import('./sites/default/pages/RegisterTypeSelect'));
const GlowOverview = lazy(() => import('./sites/default/pages/intro/GlowOverview'));
const VisionRole = lazy(() => import('./sites/default/pages/intro/VisionRole'));
const PlatformGuide = lazy(() => import('./sites/default/pages/intro/PlatformGuide'));
const IntroPageHistAdmin = lazy(() => import('./sites/default/pages/intro/IntroPageHistAdmin'));
const ProjectList = lazy(() => import('./sites/default/pages/projects/ProjectList'));
const ProjectDetail = lazy(() => import('./sites/default/pages/projects/ProjectDetail'));
const ProjectRegister = lazy(() => import('./sites/default/pages/projects/ProjectDetail/ProjectRegister'));
const ProjectApply = lazy(() => import('./sites/default/pages/projects/ProjectApply'));
const ExpertSearch = lazy(() => import('./sites/default/pages/experts/ExpertSearch'));
const ExpertOrgDetail = lazy(() => import('./sites/default/pages/experts/ExpertOrgDetail'));
const TechDemandCases = lazy(() => import('./sites/default/pages/demand/TechDemandCase/TechDemandCases'));
const TechDemandCaseDetail = lazy(() => import('./sites/default/pages/demand/TechDemandCase/TechDemandCaseDetail'));
const TechDemandCaseRegister = lazy(() => import('./sites/default/pages/demand/TechDemandCase/TechDemandCaseRegister'));
const TechDemandApplicants = lazy(() => import('./sites/default/pages/projects/ProjectDetail/TechDemandApplicants'));
const NationalResearchList = lazy(() => import('./sites/default/pages/research/NationalResearch/NationalResearchList'));
const PreReviewProjects = lazy(() => import('./sites/default/pages/research/FavoriteNationalResearch/PreReviewProjects'));
const PreReviewDetail = lazy(() => import('./sites/default/pages/research/FavoriteNationalResearch/PreReviewDetail'));
const PreReviewApplicants = lazy(() => import('./sites/default/pages/research/FavoriteNationalResearch/PreReviewApplicants'));
const CollaborationProposal = lazy(() => import('./sites/default/pages/research/CollaborationProposal'));
const ResearchCollaborationApply = lazy(() => import('./sites/default/pages/research/ResearchCollaborationApply'));
const AllianceList = lazy(() => import('./sites/default/pages/alli/AllianceList'));
const SpaceResPage = lazy(() => import('./sites/default/pages/alli/status/SpaceResPage'));
const AllianceDashboard = lazy(() => import('./sites/default/pages/alli/AllianceDashboard'));
const AllianceBoardPostFeedPage = lazy(() => import('./sites/default/pages/alli/board/AllianceBoardPostFeedPage'));
const AllianceMilestonePostFeedPage = lazy(() => import('./sites/default/pages/alli/milestone/AllianceMilestonePostFeedPage'));
const AllianceScheduleFeedPage = lazy(() => import('./sites/default/pages/alli/schedule/AllianceScheduleFeedPage'));
const AllianceSpaceCreate = lazy(() => import('./sites/default/pages/alli/create'));
const AllianceSpaceStatus = lazy(() => import('./sites/default/pages/alli/status/SpaceStatusPage'));
const AllianceSpaceDetail = lazy(() => import('./sites/default/pages/alli/status/SpaceDetailPage'));
const AllianceSpaceEdit = lazy(() => import('./sites/default/pages/alli/status/SpaceEditPage'));
const AllianceStageTransition = lazy(() => import('./sites/default/pages/alli/status/StageTransitionPage'));
const AllianceMemberManagement = lazy(() => import('./sites/default/pages/alli/members/MemberManagementPage'));
const GlowNotice = lazy(() => import('./sites/default/pages/support/glow-news/GlowNotice'));
const GlowNews = lazy(() => import('./sites/default/pages/support/glow-news/GlowNews'));
const GlowPerformanceMatching = lazy(() => import('./sites/default/pages/support/glow-performance/GlowPerformanceMatching'));
const GlowPerformanceCase = lazy(() => import('./sites/default/pages/support/glow-performance/GlowPerformanceCase'));
const GlowPerformancePortfolio = lazy(() => import('./sites/default/pages/support/glow-performance/GlowPerformancePortfolio'));
const SupportDetail = lazy(() => import('./sites/default/pages/support/SupportDetail'));
const SupportEdit = lazy(() => import('./sites/default/pages/support/SupportEdit'));
const NoticePopupWindow = lazy(() => import('./sites/default/pages/support/NoticePopupWindow'));
const SupportCenterLibrary = lazy(() => import('./sites/default/pages/support/support-center/SupportCenterLibrary'));
const SupportCenterManual = lazy(() => import('./sites/default/pages/support/support-center/SupportCenterManual'));
const SupportCenterFaq = lazy(() => import('./sites/default/pages/support/support-center/SupportCenterFaq'));
const SupportCenterInquiry = lazy(() => import('./sites/default/pages/support/support-center/SupportCenterInquiry'));
const OpenSourceNotice = lazy(() => import('./sites/default/pages/comm/OpenSourceNotice'));
const TermsOfServicePage = lazy(() => import('./sites/default/pages/legal/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./sites/default/pages/legal/PrivacyPolicyPage'));
const MyInfo = lazy(() => import('./sites/default/pages/mypage/MyInfo'));
const OrgManageInfo = lazy(() => import('./sites/default/pages/mypage/OrgManage/OrgManageInfo'));
const OrgManageRoadmap = lazy(() => import('./sites/default/pages/mypage/OrgManage/OrgManageRoadmap'));
const OrgManageManager = lazy(() => import('./sites/default/pages/mypage/OrgManage/OrgManageManager'));
const ActivityHistoryPocAlliance = lazy(() => import('./sites/default/pages/mypage/ActivityHistory/ActivityHistoryPocAlliance'));
const ActivityHistorySuccessCase = lazy(() => import('./sites/default/pages/mypage/ActivityHistory/SuccessCase/ActivityHistorySuccessCase'));
const SuccessCaseProposalDetail = lazy(() => import('./sites/default/pages/mypage/ActivityHistory/SuccessCase/SuccessCaseProposalDetail'));
const SuccessCaseProposalForm = lazy(() => import('./sites/default/pages/mypage/ActivityHistory/SuccessCase/SuccessCaseProposalForm'));
const ActivityHistoryMatching = lazy(() => import('./sites/default/pages/mypage/ActivityHistory/ActivityHistoryMatching'));
const ActivityHistoryNotificationList = lazy(() => import('./sites/default/pages/mypage/ActivityHistory/ActivityHistoryNotificationList'));
const ActivityHistoryInviteCodes = lazy(() => import('./sites/default/pages/mypage/ActivityHistory/ActivityHistoryInviteCodes'));
const RegisterApproval = lazy(() => import('./sites/default/pages/admin/registerApproval/RegisterApproval'));
const RegisterApprovalDetail = lazy(() => import('./sites/default/pages/admin/registerApproval/RegisterApprovalDetail'));
const GlowPortalPolicyPage = lazy(() => import('./sites/default/pages/admin/GlowPortalPolicyPage'));
const UserManager = lazy(() => import('./sites/default/pages/admin/userManage/UserManager'));
const UserManagerDetail = lazy(() => import('./sites/default/pages/admin/userManage/UserManagerDetail'));
const OrgManager = lazy(() => import('./sites/default/pages/admin/orgManage/OrgManager'));
const OrgManagerDetail = lazy(() => import('./sites/default/pages/admin/orgManage/OrgManagerDetail'));
const TechDemandMatchManager = lazy(() => import('./sites/default/pages/admin/TechDemandMatchManager'));
const ResearchMatchManager = lazy(() => import('./sites/default/pages/admin/ResearchMatchManager'));
const MatchingMonitoring = lazy(() => import('./sites/default/pages/admin/MatchingMonitoring'));
const AllianceMonitoring = lazy(() => import('./sites/default/pages/admin/AllianceMonitoring'));
const PerformanceSummaryManager = lazy(() => import('./sites/default/pages/admin/performanceSummary/PerformanceSummaryManager'));
const PerformanceSummaryDetail = lazy(() => import('./sites/default/pages/admin/performanceSummary/PerformanceSummaryDetail'));
const TermsManager = lazy(() => import('./sites/default/pages/admin/termsManage/TermsManager'));
const MailTemplateManager = lazy(() => import('./sites/default/pages/admin/mailTemplate/MailTemplateManager'));
const MailValidList = lazy(() => import('./sites/default/pages/admin/mailValidList/MailValidList'));
const SmtpSettingsPage = lazy(() => import('./sites/default/pages/system/SmtpSettingsPage'));
const ProgramManager = lazy(() => import('./sites/default/pages/system/ProgramManager'));
const MenuManager = lazy(() => import('./sites/default/pages/system/MenuManager'));
const PageCmsManager = lazy(() => import('./sites/default/pages/system/PageCmsManager'));
const ComponentManager = lazy(() => import('./sites/default/pages/system/ComponentManager'));
const FooterAreaManager = lazy(() => import('./sites/default/pages/system/FooterAreaManager'));
const HeaderAreaManager = lazy(() => import('./sites/default/pages/system/HeaderAreaManager'));
const DocumentFormBuilder = lazy(() => import('./sites/default/pages/report/DocumentFormBuilder'));
const InviteEntryPage = lazy(() => import('./sites/default/pages/invite/InviteEntryPage'));

function RedirectMilestoneListToDashboard() {
    const { projectId } = useParams();
    return <Navigate to={`/alli/alliance-dashboard?tab=milestone&view=list&projectId=${projectId || ''}`} replace />;
}
function RedirectMilestoneDetailToDashboard() {
    const { id } = useParams();
    return <Navigate to={`/alli/alliance-dashboard?tab=milestone&view=detail&id=${id || ''}`} replace />;
}
function RedirectMilestoneWriteToDashboard() {
    const { projectId } = useParams();
    return <Navigate to={`/alli/alliance-dashboard?tab=milestone&view=write&projectId=${projectId || ''}`} replace />;
}
function RedirectMilestoneEditToDashboard() {
    const { id } = useParams();
    return <Navigate to={`/alli/alliance-dashboard?tab=milestone&view=edit&id=${id || ''}`} replace />;
}

/** `/projects/demand/applicants/:id` 별칭 → `/demand/tech-demand-applicants/:id` */
function RedirectProjectsDemandApplicants() {
    const { id } = useParams();
    const { search } = useLocation();
    return <Navigate to={`/demand/tech-demand-applicants/${id || ''}${search}`} replace />;
}

/** MainLayout 없이 렌더되는 공지 팝업 전용 창 — 헤더·푸터·협력기관 영역 제외 */
function AppRoutes() {
    const { pathname } = useLocation();
    const isNoticePopup = isNoticePopupWindowPath(pathname);

    return (
        <RightSidebarProvider>
            <LanguageRouteSync />
            {!isNoticePopup && <ApiHealthBanner />}
            <ScrollToTop />
            {!isNoticePopup && <Popup />}
            {!isNoticePopup && <ScrollToTopBtn />}
            {!isNoticePopup && <SidebarCommon />}
            <Suspense fallback={<div className="container mt-4"><ContentSkeleton /></div>}>
                <Routes>
                    <Route element={<MainLayout />}>
                        <Route path="/" element={<Home />} />
                        {/* GLOW Platform 개요 */}
                        <Route path="/intro/glow-overview" element={<GlowOverview />} />
                        {/* 비전 & 역할 */}
                        <Route path="/intro/vision-role" element={<VisionRole />} />
                        {/* 플랫폼 이용 안내 */}
                        <Route path="/intro/platform-guide" element={<PlatformGuide />} />
                        {/* GLOW 관리자 전용 — 소개 페이지 DB 이력·게시 버전 적용 */}
                        <Route path="/intro/page-history-admin" element={<IntroPageHistAdmin />} />
                        {/* 기술수요·오픈커뮤니티 과제 — 공개 URL, 구현은 pages/projects */}
                        <Route path="/demand/tech-demand-list" element={<ProjectList />} />
                        <Route path="/demand/tech-demand-register" element={<ProjectRegister />} />
                        <Route path="/demand/tech-demand-detail/:id" element={<ProjectDetail />} />
                        <Route path="/demand/tech-demand-applicants/:id" element={<TechDemandApplicants />} />
                        <Route path="/demand/tech-demand-apply" element={<ProjectApply />} />
                        <Route path="/community/open-project-list" element={<ProjectList />} />
                        <Route path="/community/open-project-detail/:id" element={<ProjectDetail />} />
                        <Route path="/community/open-project-apply" element={<ProjectApply />} />
                        <Route path="/community/open-project-register" element={<ProjectRegister />} />
                        {/* 별칭: /projects/:portal (직접 접근·테스트용) */}
                        <Route path="/projects/demand/applicants/:id" element={<RedirectProjectsDemandApplicants />} />
                        <Route path="/projects/:portal/detail/:id" element={<ProjectDetail />} />
                        <Route path="/projects/:portal/register" element={<ProjectRegister />} />
                        <Route path="/projects/:portal/apply" element={<ProjectApply />} />
                        <Route path="/projects/:portal" element={<ProjectList />} />
                        {/* 전문가/기관 탐색 (통합: /experts/:portal) + /demand·/community 별 경로 */}
                        <Route path="/experts/:portal/detail/:id" element={<ExpertOrgDetail />} />
                        <Route path="/experts/:portal" element={<ExpertSearch />} />
                        <Route path="/demand/expert-org-detail/:id" element={<ExpertOrgDetail />} />
                        <Route path="/demand/expert-search" element={<ExpertSearch />} />
                        {/* 기술 수요 진행 사례 */}
                        <Route path="/demand/tech-demand-cases" element={<TechDemandCases />} />
                        {/* 기술 수요 진행 사례 등록 */}
                        <Route path="/demand/tech-demand-cases/register" element={<TechDemandCaseRegister />} />
                        {/* 기술 수요 진행 사례 편집 (/:id/edit 가 :id 보다 먼저 매칭되도록 위에 배치) */}
                        <Route path="/demand/tech-demand-cases/:id/edit" element={<TechDemandCaseRegister />} />
                        {/* 기술 수요 진행 사례 상세 */}
                        <Route path="/demand/tech-demand-cases/:id" element={<TechDemandCaseDetail />} />
                        <Route path="/community/expert-search" element={<ExpertSearch />} />
                        <Route path="/community/expert-org-detail/:id" element={<ExpertOrgDetail />} />
                        {/* 국가 연구사업 목록 (카드 클릭 시 NTIS 새창) */}
                        <Route path="/research/national-list" element={<NationalResearchList />} />
                        {/* 관심 사업 사전 협력 검토 */}
                        <Route path="/research/pre-review" element={<PreReviewProjects />} />
                        {/* 관심 사업 사전협력 검토 제안 상세 (받은 제안 탭 [자세히 보기...] 진입) */}
                        <Route path="/research/pre-review-detail/:id" element={<PreReviewDetail />} />
                        {/* 연구과제 협력 관리 (지원자 관리) */}
                        <Route path="/research/pre-review-detail/:id/applicants" element={<PreReviewApplicants />} />
                        {/* 사전협의 / 협력 제안 */}
                        <Route path="/research/collaboration-proposal" element={<CollaborationProposal />} />
                        {/* 전문가 / 기관 탐색 */}
                        <Route path="/research/expert-search" element={<ExpertSearch />} />
                        {/* 전문가/기관 상세 (연구사업) — 통합 ExpertOrgDetail */}
                        <Route path="/research/expert-org-detail/:id" element={<ExpertOrgDetail />} />
                        {/* 연구사업 협력 제안 참여 (Receive) */}
                        <Route path="/research/collaboration-apply" element={<ResearchCollaborationApply />} />
                        {/* Alliance Space 목록 */}
                        <Route path="/alli/alliance-list" element={<AllianceList />} />
                        {/* 알림 링크: Alliance Space 초대 수락/거절 */}
                        <Route path="/alli/space-invite-respond" element={<SpaceResPage />} />
                        {/* 비회원 초대코드 진입 */}
                        <Route path="/invite/entry" element={<InviteEntryPage />} />
                        {/* 스페이스 생성 양식 (화면 2) */}
                        <Route path="/alli/space/create" element={<AllianceSpaceCreate />} />
                        {/* 스페이스 상태별 안내 (pending | rejected | suspended | archived) — 단일 컴포넌트 분기 */}
                        <Route path="/alli/space/:id/detail" element={<AllianceSpaceDetail />} />
                        <Route path="/alli/space/:id/pending" element={<AllianceSpaceStatus />} />
                        <Route path="/alli/space/:id/rejected" element={<AllianceSpaceStatus />} />
                        <Route path="/alli/space/:id/suspended" element={<AllianceSpaceStatus />} />
                        <Route path="/alli/space/:id/archived" element={<AllianceSpaceStatus />} />
                        <Route path="/alli/space/:id/edit" element={<AllianceSpaceEdit />} />
                        <Route path="/alli/space/:spaceId/stage-transition" element={<AllianceStageTransition />} />
                        <Route path="/alli/space/:spaceId/members" element={<AllianceMemberManagement />} />
                        {/* Alliance Dashboard */}
                        <Route path="/alli/alliance-dashboard" element={<AllianceDashboard />} />
                        <Route path="/alli/alliance-board-post-feed" element={<AllianceBoardPostFeedPage />} />
                        <Route path="/alli/alliance-milestone-post-feed" element={<AllianceMilestonePostFeedPage />} />
                        <Route path="/alli/alliance-schedule-feed" element={<AllianceScheduleFeedPage />} />
                        {/* 마일스톤 목록/상세는 대시보드 내부로 리다이렉트 */}
                        <Route path="/alli/milestone-list/:projectId" element={<RedirectMilestoneListToDashboard />} />
                        <Route path="/alli/milestone-detail/:id" element={<RedirectMilestoneDetailToDashboard />} />
                        <Route path="/alli/milestone-write/:projectId" element={<RedirectMilestoneWriteToDashboard />} />
                        <Route path="/alli/milestone-edit/:id" element={<RedirectMilestoneEditToDashboard />} />
                        {/* 기술수요/연구사업 협의체 목록 페이지 제거 → Alliance 목록으로 리다이렉트 */}
                        <Route path="/alli/tech-demand-council-list" element={<Navigate to="/alli/alliance-list" replace />} />
                        <Route path="/alli/research-council-list" element={<Navigate to="/alli/alliance-list" replace />} />
                        {/* GLOW 소식 (공지사항 / GLOW NEWS 각각 페이지) */}
                        <Route path="/support/glow-news" element={<Navigate to="/support/glow-news/notice" replace />} />
                        <Route path="/support/glow-news/notice" element={<GlowNotice />} />
                        <Route path="/support/glow-news/news" element={<GlowNews />} />
                        <Route path="/support/glow-news/detail/:type/:no" element={<SupportDetail />} />
                        <Route path="/support/glow-news/edit/:type/:no" element={<SupportEdit />} />
                        {/* GLOW 성과 & 현황 (매칭·협업 / 사례 소개 / 성과·포트폴리오 각각 페이지) */}
                        <Route path="/support/glow-performance" element={<Navigate to="/support/glow-performance/matching" replace />} />
                        <Route path="/support/glow-performance/matching" element={<GlowPerformanceMatching />} />
                        <Route path="/support/glow-performance/case" element={<GlowPerformanceCase />} />
                        <Route path="/support/glow-performance/portfolio" element={<GlowPerformancePortfolio />} />
                        <Route path="/support/glow-performance/detail/:tabType/:no" element={<SupportDetail />} />
                        <Route path="/support/glow-performance/edit/:tabType/:no" element={<SupportEdit />} />
                        {/* 지원 */}
                        {/* 지원 (자료실 / 이용 매뉴얼 / FAQ / 문의하기 각각 페이지) */}
                        <Route path="/support/support-center" element={<Navigate to="/support/support-center/library" replace />} />
                        <Route path="/support/support-center/library" element={<SupportCenterLibrary />} />
                        <Route path="/support/support-center/manual" element={<SupportCenterManual />} />
                        <Route path="/support/support-center/faq" element={<SupportCenterFaq />} />
                        <Route path="/support/support-center/inquiry" element={<SupportCenterInquiry />} />
                        <Route path="/support/support-center/detail/:tabType/:no" element={<SupportDetail />} />
                        <Route path="/support/support-center/edit/:tabType/:no" element={<SupportEdit />} />
                        <Route path="/open-source-notice" element={<OpenSourceNotice />} />
                        <Route path="/terms" element={<TermsOfServicePage />} />
                        <Route path="/privacy" element={<PrivacyPolicyPage />} />

                        {/* 내 정보 관리 */}
                        <Route path="/mypage/my-info" element={<MyInfo />} />
                        {/* 기관 관리 */}
                        {/* 기관 관리 (기관 정보 / 기술 로드맵 / 기관 담당자 관리 각각 페이지) */}
                        <Route path="/mypage/org-manage" element={<Navigate to="/mypage/org-manage/info" replace />} />
                        <Route path="/mypage/org-manage/info" element={<OrgManageInfo />} />
                        <Route path="/mypage/org-manage/roadmap" element={<OrgManageRoadmap />} />
                        <Route path="/mypage/org-manage/manager" element={<OrgManageManager />} />
                        {/* 활동 내역 (수행 PoC/Alliance 이력 / 성공사례 제안 / 매칭 알림 각각 페이지) */}
                        <Route path="/mypage/activity-history" element={<Navigate to="/mypage/activity-history/poc-alliance" replace />} />
                        <Route path="/mypage/activity-history/poc-alliance" element={<ActivityHistoryPocAlliance />} />
                        <Route path="/mypage/activity-history/success-case/register" element={<SuccessCaseProposalForm />} />
                        <Route path="/mypage/activity-history/success-case/:id/edit" element={<SuccessCaseProposalForm />} />
                        <Route path="/mypage/activity-history/success-case/:id" element={<SuccessCaseProposalDetail />} />
                        <Route path="/mypage/activity-history/success-case" element={<ActivityHistorySuccessCase />} />
                        <Route path="/mypage/activity-history/matching" element={<ActivityHistoryMatching />} />
                        <Route path="/mypage/activity-history/notifications" element={<ActivityHistoryNotificationList />} />
                        <Route path="/mypage/activity-history/invite-codes" element={<ActivityHistoryInviteCodes />} />
                        {/* 등록 신청 승인/관리 */}
                        <Route path="/admin/register-approval" element={<RegisterApproval />} />
                        <Route path="/admin/register-approval/:id" element={<RegisterApprovalDetail />} />
                        <Route path="/admin/glow-portal-policy" element={<GlowPortalPolicyPage />} />
                        {/* 사용자 관리 */}
                        <Route path="/admin/user-manager" element={<UserManager />} />
                        <Route path="/admin/user-manager/:id" element={<UserManagerDetail />} />
                        {/* 기관 관리 */}
                        <Route path="/admin/org-manager" element={<OrgManager />} />
                        <Route path="/admin/org-manager/:id" element={<OrgManagerDetail />} />
                        {/* 기술 수요 및 매칭 데이터 관리 */}
                        <Route path="/admin/tech-demand-match" element={<TechDemandMatchManager />} />
                        {/* 연구사업 정보 및 매칭 데이터 관리 */}
                        <Route path="/admin/research-match" element={<ResearchMatchManager />} />
                        {/* 매칭 진행 및 성과 모니터링 */}
                        <Route path="/admin/matching-monitoring" element={<MatchingMonitoring />} />
                        {/* Alliance 현황 모니터링 */}
                        <Route path="/admin/alliance-monitoring" element={<AllianceMonitoring />} />
                        {/* 성과 집계 및 관리 */}
                        <Route path="/admin/performance-summary" element={<PerformanceSummaryManager />} />
                        <Route path="/admin/performance-summary/:uid" element={<PerformanceSummaryDetail />} />
                        {/* 약관 관리 */}
                        <Route path="/admin/terms-manager" element={<TermsManager />} />
                        <Route path="/admin/mail-templates" element={<MailTemplateManager />} />
                        {/* 이메일 인증 시도 관리(수동 인증 처리) */}
                        <Route path="/admin/mail-valid-list" element={<MailValidList />} />
                        <Route path="/admin/invite-codes" element={<Navigate to="/mypage/activity-history/invite-codes" replace />} />
                        {/* Alliance 경로 접근 차단 - 홈으로 리다이렉트 */}
                        <Route path="/alliance/*" element={<Navigate to="/" replace />} />
                        {/* 통합 페이지 라우터 - 프로그램 정보의 pageType에 따라 CMS/STATIC 자동 판단 */}
                        <Route path="/page/:pageType" element={<PageRouter />} />
                        <Route path="/sample/:pageType" element={<PageRouter />} />
                        <Route path="/datatable-sample" element={<PageRouter />} />
                        <Route path="/rich-text-demo" element={<PageRouter />} />
                        <Route path="/popup-test" element={<PageRouter />} />
                        <Route path="/db/page/:pageType" element={<PageRouter />} />
                        {/* 시스템 관리 */}
                        <Route path="/system/program" element={<ProgramManager />} />
                        <Route path="/system/menu" element={<MenuManager />} />
                        <Route path="/system/page-cms" element={<PageCmsManager />} />
                        <Route path="/system/component" element={<ComponentManager />} />
                        <Route path="/system/footer-area" element={<FooterAreaManager />} />
                        <Route path="/system/header-area" element={<HeaderAreaManager />} />
                        {/* SMTP 메일 발송 설정 */}
                        <Route path="/system/smtp-settings" element={<SmtpSettingsPage />} />
                        <Route path="/admin/smtp-settings" element={<Navigate to="/system/smtp-settings" replace />} />
                        {/* 보고서/문서폼 */}
                        <Route path="/report/form-builder" element={<DocumentFormBuilder />} />
                    </Route>
                    <Route path="/support/glow-news/notice-popup/:uid" element={<NoticePopupWindow />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<RegisterTypeSelect />} />
                    <Route path="/register/apply" element={<Register />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Suspense>
        </RightSidebarProvider>
    );
}

function App() {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const welcomeText = t('login.welcomeText');
        if (welcomeText) {
            document.title = welcomeText;
            const descriptionTag = document.querySelector('meta[name="description"]');
            if (descriptionTag) {
                descriptionTag.setAttribute('content', welcomeText);
            }
        }
    }, [t, i18n.language]);

    return (
        <AuthProvider>
            <ThemeProvider>
                <PageLayoutProvider>
                    <PopupProvider>
                        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                            <AppRoutes />
                        </BrowserRouter>
                    </PopupProvider>
                </PageLayoutProvider>
            </ThemeProvider>
        </AuthProvider>
    );
}

export default App;
